import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface GpsAlertStats {
  total: number;
  unresolved: number;
  today: number;
  thisWeek: number;
  byType: {
    geofence: number;
    speed: number;
    ignition: number;
    battery: number;
    other: number;
  };
}

export function useGpsAlertStats(companyId?: string) {
  return useQuery({
    queryKey: ['gps-alert-stats', companyId],
    queryFn: async (): Promise<GpsAlertStats> => {
      if (!companyId) {
        return {
          total: 0,
          unresolved: 0,
          today: 0,
          thisWeek: 0,
          byType: {
            geofence: 0,
            speed: 0,
            ignition: 0,
            battery: 0,
            other: 0,
          },
        };
      }

      const now = new Date();
      
      // Today
      const todayStart = new Date(now);
      todayStart.setHours(0, 0, 0, 0);

      // This week
      const weekStart = new Date(now);
      weekStart.setDate(weekStart.getDate() - 7);

      // For now, return empty alerts (will be replaced with RPC when ready)
      const allAlerts: any[] = [];

      // Calculate statistics
      const unresolved = allAlerts?.filter(a => !a.resolved).length || 0;
      const today = allAlerts?.filter(a => {
        const alertDate = new Date(a.created_at);
        return alertDate >= todayStart;
      }).length || 0;

      const thisWeek = allAlerts?.filter(a => {
        const alertDate = new Date(a.created_at);
        return alertDate >= weekStart;
      }).length || 0;

      // Count by type
      const byType = {
        geofence: 0,
        speed: 0,
        ignition: 0,
        battery: 0,
        other: 0,
      };

      allAlerts?.forEach((alert) => {
        switch (alert.alert_type) {
          case 'geofence_entry':
          case 'geofence_exit':
            byType.geofence++;
            break;
          case 'speed_limit':
            byType.speed++;
            break;
          case 'ignition_on':
          case 'ignition_off':
            byType.ignition++;
            break;
          case 'battery_low':
            byType.battery++;
            break;
          default:
            byType.other++;
        }
      });

      return {
        total: allAlerts?.length || 0,
        unresolved,
        today,
        thisWeek,
        byType,
      };
    },
    enabled: !!companyId,
    refetchInterval: 30000, // Refresh every 30 seconds
    staleTime: 20000,
  });
}
